/**
 * @file SearchContacts.js
 * @version 2.17.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2026-10-30
 *
 * 2.17.0 (2026-10-30, Cam 29 Oct — "can the contact cards look more like the order
 * cards etc. please? … make uniform"): on a rep's phone each customer is a
 * RepContactCard instead of a table row — the shell the order, invoice, PO and
 * standing order cards share. The card opens the customer; Call and Email are
 * its own buttons. The old phone card was this table restyled by CSS
 * (RepMobile.css), and its rules are gone with it. The desktop is unchanged;
 * the two never render together.
 */

import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './SearchContacts.css';
import Dropdown from './Dropdown';
import LastXeroSync from './LastXeroSync';
import Input from './Input';
import PaginationControls from './PaginationControls';
import ContactModal from './ContactModal';
import ContactEditModal from './ContactEditModal';
import ReactDOM from 'react-dom';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import LoadingBar from './LoadingBar';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useScrollAffordance from '../hooks/useScrollAffordance';
import ScrollMore from './ScrollMore';
import useColumnFilters from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import RowActionsMenu from './RowActionsMenu';
import ContactNotesModal from './ContactNotesModal';
import useReadOnly from '../hooks/useReadOnly';
import TableContextMenu from './TableContextMenu';
import EntityLink from './EntityLink';
import RepMobileFilterReset from './RepMobileFilterReset';
import RepContactCard from './RepContactCard';
import { RepDocCards } from './RepDocCard';
import useRepMobile from '../hooks/useRepMobile';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { useReveal, revealFilters } from '../utils/revealInSearch';

const fmtGBP = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// A "They owe" / "We owe" cell: past-due (aged) figure stacked over the
// not-yet-due (current) figure, with a breakdown tooltip. Dash when nothing.
/**
 * The group memberships as a list.
 *
 * The search endpoint returns them STRING_AGG'd into one ", "-joined string, so
 * this is where that becomes several values again. Tolerates an array too — the
 * single-contact endpoint sends one — so the caller does not have to know which
 * shape it is holding.
 */
const groupList = (groups) => {
  if (!groups) return [];
  const parts = Array.isArray(groups) ? groups : String(groups).split(',');
  return parts.map(g => String(g).trim()).filter(Boolean);
};

const renderOweCell = (agedRaw, currentRaw) => {
  const aged = parseFloat(agedRaw) || 0;
  const current = parseFloat(currentRaw) || 0;
  if (aged + current <= 0.005) return <span className="owe-none">-</span>;
  return (
    <div
      className="owe-figures"
      title={`Past due: ${fmtGBP(aged)}\nNot yet due: ${fmtGBP(current)}\nTotal: ${fmtGBP(aged + current)}`}
    >
      <span className={`owe-aged${aged > 0.005 ? ' has-aged' : ''}`}>{fmtGBP(aged)}</span>
      <span className="owe-current">{fmtGBP(current)}</span>
    </div>
  );
};

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight and the toolbar pill (utils/activeFilters.js).
 */
const CLEARED_FILTERS = {
  search: '', accountType: 'all', contracted: 'all',
  activeOnly: true,
  page: 1, limit: '20'
};

/**
 * Columns filterable from their header.
 *
 * Module-level for a stable object identity, and the keys must match the
 * whitelist in syncService.searchContacts — an unknown key narrows nothing
 * rather than erroring. Options are server facets over the WHOLE result set,
 * not the page. Group, Status and Rep used to be header dropdowns; they live
 * here now. Status has no column of its own — it rides under the account
 * number — so it is a section of the Acc# header, beside the Xero link that
 * shares its row in the cell.
 *
 * Name is deliberately not filterable: every name is unique, the facet list is
 * capped, and an untick from a capped list would hide everyone past the cap.
 * The search box is the way to narrow by name.
 */
const COLUMN_FILTERS = {
  status: {
    label: 'Acc#',
    sections: [
      { key: 'status', label: 'Status' },
      { key: 'xero_link', label: 'Xero link' }
    ]
  },
  sales_rep: { label: 'Rep' },
  groups: { label: 'Groups' }
};

function SearchContacts({ user }) {
  const readOnly = useReadOnly();
  // A rep's phone: a card per customer instead of the table (2.17.0).
  const repMobile = useRepMobile();
  // The contact whose notes are being edited, if any. Held here rather than in
  // the row so the dialog survives the list re-rendering underneath it.
  const [notesContact, setNotesContact] = useState(null);
  const [contacts, setContacts] = useState([]);
  // Distinct values per filterable column across the whole result set.
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(false);
  // Persisted state — survives navigating away to another page and back.
  // The contact-modal id is persisted too so the modal re-opens above the
  // (already-restored) filtered results. selectedContact is intentionally
  // NOT persisted — the modal re-fetches by id, so a stale snapshot would
  // do more harm than good.
  const [selectedContactId, setSelectedContactId] = usePersistedState(
    'searchContacts.contactModalId', null
  );
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  // Arriving from a modal's "Find in …" (utils/revealInSearch.js): the term
  // seeds the filters and the page goes back to 1 in the FIRST render's state,
  // so the mount search is the one that was asked for.
  const reveal = useReveal('search-contacts');
  const [pagination, setPagination] = usePersistedState('searchContacts.pagination', {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  }, { seed: reveal && { page: 1 } });
  // merge: a session stored before Active only existed has no activeOnly key,
  // and takes the default (on) rather than undefined. The retired keys it does
  // carry (contactGroup, contactStatus, rep) are never sent — the query is
  // built from named fields in searchContacts, not by spreading this object.
  const [filters, setFilters] = usePersistedState('searchContacts.filters', {
    search: '',
    accountType: 'all',
    contracted: 'all',
    activeOnly: true,   // on: ACTIVE only; off: every status, narrowed from Acc#
    page: 1,
    limit: '20'
  }, { merge: true, seed: revealFilters(reveal) });
  const [sortField, setSortField] = usePersistedState('searchContacts.sortField', 'company_name');
  const [sortDirection, setSortDirection] = usePersistedState('searchContacts.sortDirection', 'asc');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [archiveConfirm, setArchiveConfirm] = useState({ isOpen: false, contact: null });
  const [archiving, setArchiving] = useState(false);
  const [showSyncWarning, setShowSyncWarning] = useState(false);
  const isInitialMount = useRef(true);

  // Right-click a header to narrow the whole result set — the selection goes
  // to the server with the search, so counts and pages reflect it.
  const cols = useColumnFilters(contacts, COLUMN_FILTERS, {
    facets,
    storageKey: 'searchContacts.columnFilters',
    onChange: () => setPagination(prev => ({ ...prev, page: 1 }))
  });

  // How much of the table is still off to the right, for the cue.
  const [tableScrollRef, tableScroll] = useScrollAffordance([]);

  const ctxMenu = useTableContextMenu({
    rows: contacts,
    getRowId: (contact) => contact.id
  });

  // Second, independent menu for the They Owe / We Owe headers, where the
  // "row" is the column being right-clicked rather than a contact. The header
  // handler stops propagation, so the table's own menu never also opens.
  const oweSortMenu = useTableContextMenu();

  // Slideout buttons
  useSlideOutButton();

  // filters / pagination / sort + the open-contact-modal id are restored
  // automatically by usePersistedState above — this effect just kicks off
  // the lookup data + initial search using whatever state was restored.
  useEffect(() => {
    searchContacts(pagination.page || 1);
    // Mark initial mount as complete after a short delay to let state settle
    setTimeout(() => {
      isInitialMount.current = false;
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Typing fires a search per pause and the responses can land out of order —
  // only the newest may write the table.
  const latest = useLatestRequest();

  const searchContacts = async (page = 1) => {
    const seq = latest.begin();
    setLoading(true);
    try {
        // Named fields, not ...filters: a session restored from before the
        // header was slimmed down still carries contactGroup / rep /
        // contactStatus, and spreading it would apply them invisibly.
        const queryParams = new URLSearchParams({
        search: filters.search || '',
        accountType: filters.accountType || 'all',
        contracted: filters.contracted || 'all',
        contactStatus: filters.activeOnly !== false ? 'ACTIVE' : 'all',
        page: page,
        limit: filters.limit || '20',
        sortField: sortField,
        sortDirection: sortDirection,
        withFacets: 'true'
        });
        // Column-header filters travel with the search, so they narrow the
        // whole result set and survive paginating and re-searching.
        if (cols.queryValue) queryParams.set('columnFilters', cols.queryValue);

        const response = await fetch(`${API_URL}/api/contacts/search?${queryParams}`, {
        credentials: 'include'
        });
        const data = await response.json();
        if (!latest.isLatest(seq)) return;   // superseded by a later search
        setContacts(data.contacts || []);
        if (data.facets) setFacets(data.facets);
        setPagination(data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
    } catch (error) {
        if (!latest.isLatest(seq)) return;
        console.error('Search failed:', error);
    }
    if (latest.isLatest(seq)) setLoading(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '20' }));
    setSortField('company_name');
    setSortDirection('asc');
    // Column filters are filters too; the button says it clears all of them.
    cols.clearEverything();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any filter at all — header or column — lights the Clear Filters button.
  // Active only differs from its cleared state only when it is OFF, so that is
  // what the pill names it.
  const headerFilters = activeHeaderFilters(
    { ...filters, activeOnly: filters.activeOnly !== false },
    CLEARED_FILTERS,
    { labels: { accountType: 'Type', activeOnly: 'All statuses' } }
  );
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;

  // eslint-disable-next-line no-unused-vars
  const handleSearch = (e) => {
    e.preventDefault();
    searchContacts(1);
  };

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setShowEditModal(true);
  };

  const handleSave = () => {
    setShowEditModal(false);
    // Refresh the list - go to page 1 if adding new, stay on current page if editing
    searchContacts(selectedContact ? pagination.page : 1);
  };

  // Money columns open on descending — clicking "They Owe" means "who owes us
  // the most", not "who owes us nothing". Text columns still open A–Z.
  const DESC_FIRST_FIELDS = [
    'they_owe', 'we_owe', 'they_owe_overdue', 'we_owe_overdue', 'credit_limit'
  ];

  const handleSort = (field) => {
    if (field === sortField) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
        setSortField(field);
        setSortDirection(DESC_FIRST_FIELDS.includes(field) ? 'desc' : 'asc');
    }
  };

  // An owe column sorts on one of two bases, chosen from its header's
  // right-click menu: '' for the whole outstanding balance, '_overdue' for the
  // past-due half alone. The basis lives in the sort field itself, so it
  // persists across navigation with the rest of the sort state.
  const oweSortBasis = (column) =>
    sortField === `${column}_overdue` ? 'overdue' : sortField === column ? 'total' : null;

  // Picking a basis from the menu sets it; it never toggles direction, since a
  // menu click reads as "sort this way", not "reverse what you're doing".
  const setOweSort = (field) => {
    if (field === sortField) return;
    setSortField(field);
    setSortDirection('desc');
  };

  const buildOweSortActions = (column) => [
    {
      key: 'total',
      icon: 'functions',
      label: 'Sort by total due',
      title: 'Past due plus not yet due',
      active: sortField === column,
      onClick: () => setOweSort(column)
    },
    {
      key: 'overdue',
      icon: 'schedule',
      label: 'Sort by overdue only',
      title: 'Past due — the red figure',
      active: sortField === `${column}_overdue`,
      onClick: () => setOweSort(`${column}_overdue`)
    }
  ];

  useEffect(() => {
    if (isInitialMount.current) return;
    if (pagination.page > 0) {
        searchContacts(pagination.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  // Auto-search on filter change (skip during initial state restoration)
  useEffect(() => {
    if (isInitialMount.current) return;

    const timeoutId = setTimeout(() => {
      searchContacts(1);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // A column filter is part of the query, so changing one re-runs the search
  // the same way changing a filter above the table does.
  useEffect(() => {
    if (isInitialMount.current) return;
    searchContacts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols.queryValue]);

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      searchContacts(newPage);
    }
  };

  const syncContacts = async () => {
    setLoading(true);
    setSyncProgress(0);
    setSyncMessage('Checking environment...');

    try {
      // Safety check: verify backend is using expected database
      const envResponse = await fetch(`${API_URL}/api/env`, { credentials: 'include' });
      const envData = await envResponse.json();
      const frontendEnv = process.env.REACT_APP_ENV_NAME || 'PRODUCTION';

      if (envData.environment !== frontendEnv) {
        const mismatch = 'Server configuration mismatch.\n\nSync has been aborted to prevent data corruption. Please contact your system administrator.';
        setAlertDialog({ isOpen: true, message: mismatch, type: 'error' });
        setLoading(false);
        setSyncProgress(0);
        setSyncMessage('');
        return;
      }

      setSyncMessage('Initiating sync...');
      const eventSource = new EventSource(
        `${API_URL}/api/sync/contacts`,
        { withCredentials: true }
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'progress') {
          // Use pre-computed percentage from backend (0-65% fetching, 65-100% processing)
          const progress = data.percentage != null
            ? Math.min(99, data.percentage)
            : Math.min(95, Math.round((data.currentPage / Math.max(data.totalPages, 1)) * 100));
          setSyncProgress(progress);
          setSyncMessage(data.message || 'Syncing...');
        } else if (data.type === 'complete') {
          setSyncProgress(100);
          setSyncMessage('Sync complete!');

          const result = data.result;

          if (!result.success) {
            setAlertDialog({ isOpen: true, message: `Sync failed: ${result.error || 'Unknown error'}`, type: 'error' });
            eventSource.close();
            setTimeout(() => { setLoading(false); setSyncProgress(0); setSyncMessage(''); }, 1000);
            return;
          }

          const changedCount = (result.added || 0) + (result.updated || 0);
          let message = changedCount > 0
            ? `${result.totalInDatabase} contacts synced: ${changedCount} have changed (${result.added || 0} new, ${result.updated || 0} updated)`
            : `${result.totalInDatabase} contacts synced: all up to date`;

          // Check for orphaned contacts that were archived
          if (result.archivedLocally > 0 && result.orphanedContacts && result.orphanedContacts.length > 0) {
            const orphanedList = result.orphanedContacts
              .slice(0, 10) // Show first 10
              .map(c => `  • ${c.accountNo || 'No Acct#'} - ${c.companyName}`)
              .join('\n');

            const remainingCount = result.orphanedContacts.length > 10
              ? `\n  ... and ${result.orphanedContacts.length - 10} more`
              : '';

            message += `\n\n⚠️ WARNING: ${result.archivedLocally} contact(s) archived locally because they were not found in Xero (may be archived in Xero):\n\n${orphanedList}${remainingCount}\n\nPlease verify these contacts are archived in Xero.`;
          }

          setAlertDialog({ isOpen: true, message, type: result.archivedLocally > 0 ? 'warning' : 'success' });
          searchContacts(1);
          eventSource.close();

          setTimeout(() => {
            setLoading(false);
            setSyncProgress(0);
            setSyncMessage('');
          }, 1000);
        } else if (data.type === 'error') {
          setAlertDialog({ isOpen: true, message: `Sync failed: ${data.error}`, type: 'error' });
          eventSource.close();
          setLoading(false);
          setSyncProgress(0);
          setSyncMessage('');
        }
      };

      eventSource.onerror = () => {
        setAlertDialog({ isOpen: true, message: 'Connection to server lost during sync', type: 'error' });
        eventSource.close();
        setLoading(false);
        setSyncProgress(0);
        setSyncMessage('');
      };
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to sync: ${error.message}`, type: 'error' });
      setLoading(false);
      setSyncProgress(0);
      setSyncMessage('');
    }
  };

  const handleArchiveClick = (contact) => {
    setArchiveConfirm({ isOpen: true, contact });
  };

  const handleArchiveConfirm = async (archiveInXero) => {
    const contact = archiveConfirm.contact;
    setArchiveConfirm({ isOpen: false, contact: null });

    if (!contact) return;

    // Always archive in Xero if the contact has a Xero ID
    const shouldArchiveInXero = archiveInXero || !!contact.xero_contact_id;

    setArchiving(true);
    try {
      const response = await fetch(`${API_URL}/api/contacts/${contact.id}/archive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ archiveInXero: shouldArchiveInXero })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to archive contact');
      }

      const message = data.archivedInXero
        ? `"${contact.company_name}" archived in both the database and Xero.`
        : `"${contact.company_name}" archived locally.`;

      setAlertDialog({ isOpen: true, message, type: 'success' });
      searchContacts(pagination.page);
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to archive: ${error.message}`, type: 'error' });
    } finally {
      setArchiving(false);
    }
  };

  // Shared by the row actions menu and the right-click context menu so the two
  // surfaces can never drift apart.
  const buildRowActions = (contact) => [
    { key: 'view', readSafe: true, icon: 'visibility', label: 'View', title: 'View', onClick: () => setSelectedContactId(contact.id) },
    /* A view-only account gets the notes editor in this slot instead of the
       full one: notes are the single field they may write, and the row keeps
       its action count either way — the project's rule that a row's buttons do
       not change in number between row types or between users. readSafe,
       because applyReadOnly would otherwise disable the one thing they CAN do. */
    readOnly
      ? { key: 'edit', readSafe: true, icon: 'edit_note', label: 'Edit notes',
          disabled: contact.contact_status === 'ARCHIVED',
          title: contact.contact_status === 'ARCHIVED' ? 'Cannot edit — contact is archived' : 'Edit customer notes',
          onClick: () => { if (contact.contact_status !== 'ARCHIVED') setNotesContact(contact); } }
      : { key: 'edit', icon: 'edit', label: 'Edit', disabled: contact.contact_status === 'ARCHIVED', title: contact.contact_status === 'ARCHIVED' ? 'Cannot edit — contact is archived' : 'Edit', onClick: () => { if (contact.contact_status !== 'ARCHIVED') handleEdit(contact); } },
    contact.contact_status !== 'ARCHIVED'
      ? { key: 'archive', icon: 'delete', label: 'Archive', className: 'btn-danger', disabled: archiving, title: 'Archive', onClick: () => { if (!archiving) handleArchiveClick(contact); } }
      : { key: 'unarchive', icon: 'unarchive', label: 'Restore', disabled: true, title: 'Cannot restore — contact must be restored in Xero first' },
  ];

  return (
    <div className="contact-search">
      {/* The rep phone shell hides .search-header entirely — see RepMobile.css
          — and this stands in for it. Desktop renders nothing. */}
      <RepMobileFilterReset onReset={handleClearFilters} />
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Search, add, and manage customer and supplier contacts.</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>Add contacts from this header. View contact details, delivery addresses, credit limits, and transaction history.<LastXeroSync entity="contacts" /></span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            {user?.is_admin && (
              <>
              <div>
                <button data-write-action onClick={() => setShowSyncWarning(true)} className="btn-slideout admin-item" disabled={loading}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">sync</span>
                  </div>
                  <span className="btn-label">Sync from Xero</span>
                </button>
              </div>
              </>
            )}
            <div>
              <button data-write-action onClick={() => { setSelectedContact(null); setShowEditModal(true); }} className="btn-slideout">
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add</span>
              </button>
            </div>
          </div>

        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp
                fields={['name', 'address', 'email', 'account no.', 'phone / mobile']}
              />
            </label>
            <Input
                type="text"
                placeholder="Name, account, address, email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onEnter={() => searchContacts(1)}
                icon="search"
            />
          </div>

          <div className="filter-group">
            <label>Account Type</label>
            <Dropdown
                value={filters.accountType}
                onChange={(value) => handleFilterChange('accountType', value)}
                options={[
                    { value: 'all', label: 'All Accounts' },
                    { value: 'CUSTOMER', label: 'Customer' },
                    { value: 'SUPPLIER', label: 'Supplier' }
                ]}
                placeholder="Select account type..."
            />
          </div>

          <div className="filter-group">
            <label>Contracted</label>
            <Dropdown
                value={filters.contracted}
                onChange={(value) => handleFilterChange('contracted', value)}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'YES', label: 'Yes' },
                  { value: 'NO', label: 'No' },
                ]}
                placeholder="Select..."
            />
          </div>

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
                value={filters.limit}
                onChange={(value) => handleFilterChange('limit', value)}
                options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
                ]}
            />
          </div>

          {/* On: ACTIVE contacts only, as the old Status dropdown's default.
              Off: every status — then the Status section of the Acc# header
              narrows to archived, or whatever else is wanted. */}
          <div className="filter-group">
            <label>Active only</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="contacts-active-only"
                  type="checkbox"
                  checked={filters.activeOnly !== false}
                  onChange={(e) => handleFilterChange('activeOnly', e.target.checked)}
                />
                <label htmlFor="contacts-active-only"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {syncProgress > 0 && (
        <div className="loading-bar-wrapper">
          <LoadingBar progress={syncProgress} message={syncMessage} />
        </div>
      )}

      <div className="contacts-panel">
        {/* One toolbar row, as on every search screen: the filter pill, then
            "Showing…" and the page controls taking the rest. */}
        <div className="table-toolbar">
          <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />
          {pagination && pagination.total > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={changePage}
            />
          )}
        </div>

        {repMobile ? (
          <RepDocCards loading={loading} count={cols.rows.length} empty="No contacts found">
            {cols.rows.map(contact => (
              <RepContactCard
                key={contact.id}
                contact={contact}
                onOpen={() => setSelectedContactId(contact.id)}
              />
            ))}
          </RepDocCards>
        ) : (
        /* Wrapped so the horizontal cue can sit still against the
           visible edge — inside the scrolling box it would travel
           with the columns and leave the screen. */
        <div className="scroll-more-wrap">
        <div className="table-container scroll-more-box" ref={tableScrollRef}>
          <table className="data-table table-min-lg" onContextMenu={ctxMenu.onTableContextMenu}>
            <thead>
              <tr>
                {/* Status has no column of its own any more — it rides under the
                    account number, so its filter rides on that header too. */}
                <th className={`sortable ${cols.headerProps('status').className || ''}`.trim()}
                    onClick={() => handleSort('account_no')}
                    onContextMenu={cols.headerProps('status').onContextMenu}
                    title="Right-click to filter by status or Xero link">
                  ACC# {sortField === 'account_no' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable"
                    onClick={() => handleSort('company_name')}>
                  Name {sortField === 'company_name' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className={`sortable ${cols.headerProps('sales_rep').className || ''}`.trim()}
                    onClick={() => handleSort('sales_rep')}
                    onContextMenu={cols.headerProps('sales_rep').onContextMenu}
                    title="Right-click to filter by rep">
                  Rep {sortField === 'sales_rep' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                {[
                  { field: 'they_owe', label: 'They Owe', hint: 'Owed to us (customer invoices)' },
                  { field: 'we_owe', label: 'We Owe', hint: 'Owed by us (supplier bills)' }
                ].map(({ field, label, hint }) => {
                  const basis = oweSortBasis(field);
                  return (
                    <th
                      key={field}
                      className="owe-col sortable"
                      onClick={() => handleSort(field)}
                      onContextMenu={oweSortMenu.onRowContextMenu(field)}
                      title={`${hint} — past due over not-yet-due.\nClick to sort, right-click to sort by total or overdue only.`}
                    >
                      <div className="owe-th">
                        <span>{label} {basis && (sortDirection === 'asc' ? '▲' : '▼')}</span>
                        {basis === 'overdue' && <span className="owe-basis">overdue</span>}
                      </div>
                    </th>
                  );
                })}
                <th className="sortable" onClick={() => handleSort('credit_limit')}>
                  Credit Limit {sortField === 'credit_limit' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className={`sortable ${cols.headerProps('groups').className || ''}`.trim()}
                    onClick={() => handleSort('groups')}
                    onContextMenu={cols.headerProps('groups').onContextMenu}
                    title="Right-click to filter by group">
                  Groups {sortField === 'groups' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className='actions-col'></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : cols.rows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-results">No contacts found</td>
                </tr>
              ) : (
                cols.rows.map(contact => (
                  <tr key={contact.id} onContextMenu={ctxMenu.onRowContextMenu(contact)}>
                    {/* Two stacked rows: what identifies the account, then what
                        state it is in. Status used to have a column of its own,
                        which spent 110px on a word that is "ACTIVE" for almost
                        every row. */}
                    <td data-label="Acc" className="acc-col clickable-cell" onClick={() => setSelectedContactId(contact.id)}>
                      <div className="acc-stack">
                        <div className="acc-stack-row">
                          {contact.account_no && (
                            <span className='acc-code'>
                              {contact.account_no}
                            </span>
                          )}
                          {contact.contracted && (
                            <span
                              className="contracted-tick"
                              title="Contracted"
                            >
                              <span className="material-symbols-rounded">check</span>
                            </span>
                          )}
                          {/* Parent/Child account indicator */}
                          {contact.isParent && (
                            <span
                              className="material-symbols-rounded acc-link-icon is-parent"
                              title="Parent Account"
                            >
                              link
                            </span>
                          )}
                          {contact.parentAccountNo && (
                            <span
                              className="material-symbols-rounded acc-link-icon"
                              title={`Child of ${contact.parentAccountNo}`}
                            >
                              link
                            </span>
                          )}
                        </div>
                        <div className="acc-stack-row">
                          <span className={`status-badge status-badge-sm ${contact.contact_status === 'ARCHIVED' ? 'status-archived' : 'status-active'}`}>
                            {contact.contact_status || 'ACTIVE'}
                          </span>
                          {!contact.xero_contact_id && (
                            <span className="material-symbols-rounded broken-link-icon" title="Not linked to Xero">link_off</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="clickable-cell" onClick={() => setSelectedContactId(contact.id)}>
                      <div>{contact.company_name}</div>
                      {contact.deliveryAddress && (
                        <div className="address-line">{contact.deliveryAddress}</div>
                      )}
                    </td>
                    <td data-label="Rep" className="clickable-cell" onClick={() => setSelectedContactId(contact.id)}>
                      {contact.sales_rep
                        ? <EntityLink type="sales_rep" id={contact.rep_id}>{contact.sales_rep}</EntityLink>
                        : '-'}
                    </td>
                    <td data-label="They owe" className="owe-col clickable-cell" onClick={() => setSelectedContactId(contact.id)}>
                      {renderOweCell(contact.they_owe_aged, contact.they_owe_current)}
                    </td>
                    <td data-label="We owe" className="owe-col clickable-cell" onClick={() => setSelectedContactId(contact.id)}>
                      {renderOweCell(contact.we_owe_aged, contact.we_owe_current)}
                    </td>
                    <td data-label="Credit limit" className="clickable-cell" onClick={() => setSelectedContactId(contact.id)}>
                      {contact.credit_limit && parseFloat(contact.credit_limit) > 0
                        ? `£${parseFloat(contact.credit_limit).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '-'}
                    </td>
                    {/* One badge per group, wrapping. The API sends them as a
                        single ", "-joined string, which rendered as a run of
                        text that read as one long value rather than several
                        memberships. */}
                    <td data-label="Groups" className="groups-col clickable-cell" onClick={() => setSelectedContactId(contact.id)}>
                      {groupList(contact.groups).length ? (
                        <div className="group-badges">
                          {groupList(contact.groups).map(g => (
                            <span key={g} className="account-badge-inline group-badge" title={g}>{g}</span>
                          ))}
                        </div>
                      ) : '-'}
                    </td>
                    <td className='actions-col'>
                      <RowActionsMenu actions={buildRowActions(contact)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          <ScrollMore scrollRef={tableScrollRef} remaining={tableScroll.remainingX} direction="right" />
        </div>
        )}
      </div>

      {/* Table context menu (right-click) */}
      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          {
            key: 'add',
            icon: 'add',
            label: 'Add contact',
            onClick: () => { setSelectedContact(null); setShowEditModal(true); }
          },
          {
            key: 'refresh',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'refresh',
            label: 'Refresh results',
            disabled: loading,
            onClick: () => searchContacts(pagination.page || 1)
          },
          {
            key: 'clear-filters',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: handleClearFilters
          }
        ]}
      />

      {/* Sort-basis menu for the They Owe / We Owe headers */}
      <TableContextMenu ctx={oweSortMenu} rowActions={buildOweSortActions} />

      {notesContact && ReactDOM.createPortal(
        <ContactNotesModal
          contact={notesContact}
          onClose={() => setNotesContact(null)}
          onSaved={() => setNotesContact(null)}
        />,
        document.body
      )}

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal
          contactId={selectedContactId}
          onClose={() => { setSelectedContactId(null); searchContacts(pagination.page); }}
          onEdit={(contact) => {
            setSelectedContactId(null);
            setSelectedContact(contact);
            setShowEditModal(true);
          }}
        />,
        document.body
      )}

      {showEditModal && ReactDOM.createPortal(
        <ContactEditModal
          contact={selectedContact}
          onClose={() => { setShowEditModal(false); searchContacts(pagination.page); }}
          onSave={handleSave}
        />,
        document.body
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={showSyncWarning}
        title="Sync Contacts from Xero"
        message="This will overwrite all local contact data with the latest from Xero. Any local changes not yet pushed to Xero will be lost."
        type="warning"
        confirmText="Sync"
        onConfirm={() => { setShowSyncWarning(false); syncContacts(); }}
        onCancel={() => setShowSyncWarning(false)}
      />

      {/* Archive Confirmation Dialog */}
      {archiveConfirm.isOpen && archiveConfirm.contact && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setArchiveConfirm({ isOpen: false, contact: null })}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '500px', padding: '24px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span className="material-symbols-rounded" style={{ fontSize: '32px', color: 'var(--color-warning)' }}>
                archive
              </span>
              <h3 style={{ margin: 0 }}>Archive Contact</h3>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: '1.6' }}>
              Are you sure you want to archive <strong>"{archiveConfirm.contact.company_name}"</strong>?
            </p>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: '1.6' }}>
              Archived contacts won't appear in default searches. Turn off Active only to see them.
            </p>

            {archiveConfirm.contact.xero_contact_id && (
              <div style={{
                padding: '12px',
                background: 'rgba(var(--color-info-rgb), 0.12)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '20px'
              }}>
                <p style={{ color: 'var(--color-info)', margin: 0, fontSize: 'var(--font-sm)' }}>
                  This will also archive the contact in Xero.
                </p>
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              borderTop: '1px solid var(--border-primary)',
              paddingTop: '16px'
            }}>
              <button
                className="btn-secondary"
                onClick={() => setArchiveConfirm({ isOpen: false, contact: null })}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => handleArchiveConfirm(false)}
                disabled={archiving}
              >
                {archiving ? 'Archiving...' : 'Archive Contact'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <ColumnFilterMenu cols={cols} />
    </div>
  );
}

export default SearchContacts;